import { useSyncExternalStore } from 'react';
import { cn } from 'cn';
import { DropdownMenu, Popover } from 'radix-ui';
import { THEMES, useTheme, type Theme } from '@/lib/theme';
import { useStoredPreference } from '@/features/reader/useStoredPreference';
import { useUrlParam } from '@/features/reader/useUrlState';
import { isStep, READER_STEPS, type ReaderStep } from '@/features/reader/readerSettings';

/**
 * The product's one piece of global chrome: the name, and who you are.
 *
 * The brand was nowhere on screen. Eleven screens each began with a 56px icon
 * rail carrying a mark, and nothing anywhere said *Almimbar* — a reader who
 * arrived on a shared link had no way to learn what they were using.
 *
 * **Sticky on a desktop, collapsing on a phone.** Its HEIGHT collapses, not
 * its position: a transform moves the bar and leaves its box in flow, so the
 * content below stays put and the reader gets a band of empty background
 * instead of 48px of khutbah. That is what this did until it was measured.
 * Quran.com hides its bar as you
 * scroll because its whole page scrolls and vertical space is the scarce
 * thing. Here only six of the screens work that way; the reader's shell does
 * not scroll at all — rail, toolbar and chat stay put while the khutbah moves
 * — so hiding 48px on a wide display reclaims nothing and costs the brand and
 * the account menu. On a 390px phone the space is worth having, so there it
 * hides going down and returns coming up.
 *
 * The reader also already collapses its own title on scroll. Two things
 * retracting at once reads as the page coming apart, which is why this one
 * holds still wherever that one moves.
 */

/** Who is signed in. Null until brief §18's authentication question is answered. */
export interface SignedInUser {
  readonly name: string;
  readonly email?: string;
}

/**
 * The scroll direction, as an external store.
 *
 * `useSyncExternalStore` rather than state written from an effect, for the
 * same reason `useUrlParam` and the theme provider use it: the scroll
 * position lives outside React, and subscribing to it properly is what keeps
 * it from being two sources of truth. Writing it into state from an effect
 * also trips `react-hooks/set-state-in-effect`, and the rule is right.
 *
 * One listener for the whole application, in the capture phase: the scrolling
 * element differs by screen — a fixed shell scrolls its own `<main>`, a plain
 * page scrolls the document — and capturing catches both without every screen
 * having to report upwards.
 */
let scrolledDown = false;
let lastTop = 0;
const scrollListeners = new Set<() => void>();

function handleScroll(event: Event) {
  const target = event.target;
  const top =
    target instanceof HTMLElement ? target.scrollTop : window.scrollY;
  const previous = lastTop;
  lastTop = top;
  // A few pixels is a thumb resting, not an intention to hide the bar.
  if (Math.abs(top - previous) < 8) return;
  const next = top > previous && top > 64;
  if (next === scrolledDown) return;
  scrolledDown = next;
  for (const listener of scrollListeners) listener();
}

function subscribeScroll(onChange: () => void) {
  scrollListeners.add(onChange);
  if (scrollListeners.size === 1) {
    window.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true,
    });
  }
  return () => {
    scrollListeners.delete(onChange);
    if (scrollListeners.size === 0) {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      scrolledDown = false;
      lastTop = 0;
    }
  };
}

/** A media query, subscribed to rather than mirrored into state. */
function subscribePhone(onChange: () => void) {
  const query = window.matchMedia('(max-width: 639px)');
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function isPhone(): boolean {
  return window.matchMedia('(max-width: 639px)').matches;
}

const THEME_LABELS: Record<Theme, string> = {
  system: 'Follow the system',
  light: 'Light',
  dark: 'Dark',
  paper: 'Paper',
  'high-contrast': 'High contrast',
};

const menuItem =
  'flex min-h-9 cursor-pointer select-none items-center justify-between rounded-md ' +
  'px-2 py-2 text-sm outline-none data-[highlighted]:bg-accent ' +
  'data-[highlighted]:text-accent-foreground';

/**
 * Theme, as a menu rather than the rail's cycling moon.
 *
 * It was one button at the foot of a 56px column that advanced to the next
 * theme. Five options behind a control that can only step forward cannot be
 * aimed: a reader on Light wanting Paper has to guess how many presses they
 * are away and watch the whole interface repaint on each one. The same
 * argument retired the reader's two-state width toggle.
 */
function ThemeMenu() {
  const { theme, setTheme } = useTheme();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Theme: ${THEME_LABELS[theme]}`}
          className={cn(
            'flex size-9 items-center justify-center rounded-md',
            'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <svg
            viewBox="0 0 24 24"
            width={18}
            height={18}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 3a9 9 0 1 0 9 9 7 7 0 0 1-9-9z" />
          </svg>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-52 rounded-lg border border-border bg-popover p-1 shadow-2"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Theme
          </DropdownMenu.Label>
          {THEMES.map((option) => (
            <DropdownMenu.Item
              key={option}
              className={menuItem}
              onSelect={() => setTheme(option)}
            >
              <span>{THEME_LABELS[option]}</span>
              {/* A mark AND the selected row's weight: never colour alone. */}
              {option === theme ? (
                <span aria-hidden className="text-primary">
                  ✓
                </span>
              ) : null}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Reading text size, shown only where it changes something.
 *
 * It drives `--reader-step`, which the reader and the learning screen scale
 * every script from. On the library or the settings screen it would be a
 * control that does nothing — worse than an absent one, because a reader
 * presses it and concludes the product is broken rather than that the button
 * does not apply here.
 *
 * The same stored key the reader's own `Aa` panel writes, and
 * `useStoredPreference` notifies every subscriber, so the two never disagree.
 */
function TextSize() {
  const [stored, setStored] = useStoredPreference('mimbar.reader.step', '3', isStep);
  const step = Number(stored) as ReaderStep;
  const index = READER_STEPS.indexOf(step);
  const smaller = READER_STEPS[Math.max(0, index - 1)] ?? step;
  const larger = READER_STEPS[Math.min(READER_STEPS.length - 1, index + 1)] ?? step;

  const button =
    'flex size-9 items-center justify-center rounded-md text-muted-foreground ' +
    'hover:bg-muted hover:text-foreground disabled:opacity-40 ' +
    'disabled:hover:bg-transparent disabled:hover:text-muted-foreground';

  return (
    <div role="group" aria-label="Reading text size" className="flex items-center">
      <button
        type="button"
        aria-label="Smaller text"
        disabled={step === READER_STEPS[0]}
        onClick={() => setStored(String(smaller) as `${ReaderStep}`)}
        className={button}
      >
        <span aria-hidden className="text-xs font-semibold">
          A
        </span>
      </button>
      {/* Said in words, not only by the two buttons' states. */}
      <span className="sr-only" aria-live="polite">
        Text size {step} of {READER_STEPS.length}
      </span>
      <button
        type="button"
        aria-label="Larger text"
        disabled={step === READER_STEPS[READER_STEPS.length - 1]}
        onClick={() => setStored(String(larger) as `${ReaderStep}`)}
        className={button}
      >
        <span aria-hidden className="text-base font-semibold">
          A
        </span>
      </button>
    </div>
  );
}

export function AppBar({ user = null }: { user?: SignedInUser | null }) {
  // Server snapshots are the honest defaults: during prerender there is no
  // viewport to measure and nothing has scrolled.
  const phone = useSyncExternalStore(subscribePhone, isPhone, () => false);
  const scrolled = useSyncExternalStore(
    subscribeScroll,
    () => scrolledDown,
    () => false,
  );
  // The bar only retracts where retracting buys something. See the file
  // comment: on a desktop the shell does not scroll, so hiding it reclaims
  // nothing and costs the brand and the account menu.
  const hidden = phone && scrolled;

  // Text size only appears where it changes something: the reader, and the
  // learning screen, which scale every script from `--reader-step`.
  const [khutbahId] = useUrlParam('khutbah', '');
  const [screen] = useUrlParam('screen', '');
  const reading =
    screen === 'learn' || (khutbahId !== '' && screen !== 'chat' && screen !== 'translate');

  return (
    <header
      className={cn(
        'z-30 flex shrink-0 items-center justify-between gap-3 overflow-hidden',
        'border-b border-border bg-card px-3 sm:px-4',
        // HEIGHT, not a transform.
        //
        // It retracted with `-translate-y-full`, which moves the bar and
        // leaves its 48px box exactly where it was: the content below never
        // moved up, so scrolling on a phone slid the bar away and left a band
        // of bare background where it had been. It reclaimed nothing, which
        // was the entire reason for retracting it.
        //
        // Collapsing the height takes the box with it. The children keep
        // their own height, which is why `overflow-hidden` is here — without
        // it they would spill out of a zero-height bar.
        phone ? 'transition-[height] motion-reduce:transition-none' : 'h-12',
        phone && (hidden ? 'h-0 border-b-0' : 'h-12'),
      )}
      style={phone ? { transitionDuration: 'var(--duration-base)' } : undefined}
    >
      <a href="?screen=home" className="flex items-center rounded-sm">
        {/* Two inks rather than a filter: the on-dark lockup swaps the steps
            to the Dark theme's lighter teal, per the brand book. */}
        <img
          src="/brand/lockup.svg"
          alt="Almimbar — home"
          height={24}
          className="h-6 w-auto dark:hidden"
        />
        <img
          src="/brand/lockup-on-dark.svg"
          alt="Almimbar — home"
          height={24}
          className="hidden h-6 w-auto dark:block"
        />
      </a>

      <div className="flex items-center gap-1">
        {/* Hidden rather than disabled where it would do nothing. */}
        {reading ? <TextSize /> : null}
        <ThemeMenu />
        <span aria-hidden className="mx-1 h-5 w-px bg-border" />

        {user === null ? (
          /*
            "Guest", not "Sign in".

            There are no accounts yet (brief §18 question 2 is open), so a
            sign-in control offers an action the product cannot perform.
            "Guest" states the true condition instead, and the panel says what
            it means and what will change when accounts exist — more use to a
            reader wondering where their saved khutbahs live than a button
            that opens nothing.

            A Popover, not a conditional div. The div had no Escape, no
            outside-click dismissal and moved focus nowhere: it opened, and
            the only way out was to find the trigger and press it again.
            Radix brings all three, plus aria-haspopup and focus returned to
            the trigger on close, and it is already a dependency here.
          */
          <Popover.Root>
            <Popover.Trigger
              aria-label="Guest — accounts are not built yet"
              className={cn(
                'flex min-h-9 items-center gap-2 rounded-md px-2 text-sm',
                'text-muted-foreground hover:bg-muted hover:text-foreground',
                'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
              )}
            >
              <span
                aria-hidden
                className="flex size-6 items-center justify-center rounded-full bg-muted"
              >
                <svg
                  viewBox="0 0 24 24"
                  width={13}
                  height={13}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M5 20a7 7 0 0 1 14 0" />
                </svg>
              </span>
              <span>Guest</span>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                align="end"
                sideOffset={6}
                className="z-50 w-72 rounded-lg border border-border bg-popover p-3 shadow-2 outline-none"
              >
                <h2 className="text-sm font-semibold">
                  You are reading as a guest
                </h2>
                <p className="mt-1.5 text-[13px] text-pretty text-muted-foreground">
                  Accounts are not built yet, and published khutbahs need none.
                  What you save and what you have read live in this browser, so
                  clearing it clears them, and they do not follow you to another
                  device.
                </p>
                <p className="mt-2 text-[13px] text-pretty text-muted-foreground">
                  Signing in will matter for your own uploads and for progress
                  that is yours rather than this browser&rsquo;s.
                </p>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        ) : (
          /* A menu of actions, so a menu: roving focus, typeahead and Escape
             come with it, and "Sign out" is reachable by keyboard. */
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              className={cn(
                'flex min-h-9 items-center gap-2 rounded-md px-2 text-sm hover:bg-muted',
                'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
              )}
            >
              <span
                aria-hidden
                className="flex size-6 items-center justify-center rounded-full bg-primary text-[11px] text-primary-foreground"
              >
                {user.name.slice(0, 1).toUpperCase()}
              </span>
              <span className="max-w-32 truncate">{user.name}</span>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="z-50 min-w-60 rounded-lg border border-border bg-popover p-1 shadow-2"
              >
                {user.email ? (
                  <DropdownMenu.Label className="truncate px-2 py-1.5 text-[11px] text-muted-foreground">
                    {user.email}
                  </DropdownMenu.Label>
                ) : null}
                <DropdownMenu.Item className={menuItem} asChild>
                  <a href="?screen=settings">Profile and settings</a>
                </DropdownMenu.Item>
                <DropdownMenu.Item className={menuItem} asChild>
                  <a href="?screen=library">My library</a>
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
                <DropdownMenu.Item className={cn(menuItem, 'text-destructive')}>
                  Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
    </header>
  );
}
